import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';

import { EditUserRoleComponent } from './edit-user-role/edit-user-role.component';
import { SchoolService } from '../../school/school.service';
import { Role, RolePayload } from '../../../models/role.model';
import { ProdetailsModalComponent } from '../../data-management/prodetails-modal/prodetails-modal.component';
import { MatDialogCloseEvent } from '../../../const';
import { ToastrService } from 'ngx-toastr';
import { ConfirmComponent } from '../../../shared/components/confirm/confirm.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Pagination } from '../../../models/mat-account.model';
import { FormGroup, FormArray } from '@angular/forms';
import { SharedService } from '../../../shared/services/shared.service';
import { DtoFeature, DtoPreviewItem } from '../../../models/package-center.model';

export enum RoleMaster { SuperAdmin = "1", AccountAdmin = "2", MatAdmin = "3", SchoolAdmin = "4"}

@Component({
  selector: 'app-user-role',
  templateUrl: './user-role.component.html'
})
export class UserRoleComponent implements OnInit, OnChanges {
  @Output() resetCreateTrigger = new EventEmitter<void>();
  @Input() isCurrentTab: string = "";
  @Input() clientAccountId: number = 0;
  @Input() createTriggered: boolean = false;
  @Input() packageId: number = 0;
  @Input() searchTriggered: boolean = false;
  @Input() searchTerm: string = "";
  @Input() schoolId: number = 0;

  roles: Role[] = [];
  userRole: string[] = [];
  rolesMaster = RoleMaster;
  roleName: string[] = [];
  userEmail: string = "";
  requestNewRole: boolean = false;
  roleForm = new FormGroup({
    roleDetails: new FormArray([]),
  });
  createNewRole: boolean = false;
  canManageRole: boolean = false;

  constructor(private dialog: MatDialog, private router: Router,
              private schoolService: SchoolService,
              private toasterService: ToastrService,
              private sharedService: SharedService
  ){
    this.getClaims();
  }
  
  ngOnInit(): void {

    this.userEmail = sessionStorage.getItem('user_email')!;
    this.userRole = sessionStorage.getItem('user_role')?.split(',')!;
    this.userRole?.forEach(element => {
      this.roleName.push(this.getRoleNameByValue(RoleMaster, element)!);
    });
    
    if(this.userRole.includes(this.rolesMaster.SchoolAdmin) || this.userRole.includes(this.rolesMaster.MatAdmin)){
      this.getRolesForAdmin(this.userEmail);
      this.getAssignedPackageId(this.userEmail);
      this.sharedService.roleFormValidity.subscribe(roleForm => {
        this.roleForm = roleForm;
      });
    }
    else{
      this.getRoles(this.clientAccountId);
      this.canManageRole = this.userRole.includes(this.rolesMaster.SuperAdmin) || this.userRole.includes(this.rolesMaster.AccountAdmin);
    }
  }

  getClaims() {
    const decodedToken = this.sharedService.getLoginToken();

    // Access role claim
    if(decodedToken?.orgType==='ORG'){
      this.schoolId = parseInt(decodedToken?.orgId)
    }
  }

  getRoleNameByValue<T extends { [index: string]: string }>(
    enumObj: T,
    value: string
  ): string | undefined {
    const keys = Object.keys(enumObj).filter((x) => enumObj[x] === value);
    return keys.length > 0 ? keys[0] : undefined;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.createTriggered) {
      this.openEditRolePopup({} as Role, this.packageId);
    }
    if(this.searchTriggered){
      let payload: Pagination = {
        searchTerm: this.searchTerm,
        sortColumn: "",
        sortDirection: "",
        pageOffset: 0,
        pageSize: 0,
        userRole: []
      }
      this.getRoles(this.clientAccountId, payload);
    }
  }

  getRoles(clientAccountId: number, payload = {} as Pagination) {
    let rolePayload = {
      isIndividualEntity : clientAccountId == 0,
      clientAccountId : clientAccountId > 0 ? [clientAccountId] : [this.schoolId],
      pagination : payload
    } as RolePayload;
    this.schoolService.getRoles(rolePayload).subscribe((response: Role[] | any) => {
    //this.schoolService.getRoles(clientAccountId, payload).subscribe((response: Role[] | any) => {
      this.roles = response;
      this.searchTriggered=false;
    },(err: HttpErrorResponse)=>{
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    });
  }

  getRolesForAdmin(emailId: string) {
    this.schoolService.getRolesForAdmin(emailId).subscribe((response: Role[] | any) => {
      this.roles = response;
    },(err: HttpErrorResponse)=>{
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    });
  }

  getAssignedPackageId(emailId: string) {
    this.schoolService.getAssignedPackageId(emailId).subscribe((response: any) => {
      this.packageId = response.packageId;
      if(response.organisationAccountType=="CA"){
        this.clientAccountId = response.clientAccountId;
        //this.schoolId = 0;
      }
      else{
        this.clientAccountId = 0;
        this.schoolId = response.clientAccountId;
      }

      //The account holds the Audience structure
        this.canManageRole = (
          (this.clientAccountId==0 && this.userRole.includes(this.rolesMaster.SchoolAdmin)) || 
          (this.clientAccountId!=0 && this.userRole.includes(this.rolesMaster.MatAdmin)));
    },(err: HttpErrorResponse)=>{
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    });
  }

  previewPopup(control: string, roleDetails: Role = {} as Role){
    this.dialog.open(ProdetailsModalComponent,{
      width: '1000px',
      panelClass: 'modal-prodetails',
      data: {
        singleItemPreview: true,
        itemToPreview: this.getPreviewData(control, roleDetails),
        title: 'Added ' + control + 's'
      }
    })
  }
  
  getGroupedFeatures(features: DtoFeature[]):{[key:string]: DtoFeature[]}
  {
    return features.reduce((result, feature) => 
      {
        const key = feature.featureSetName;
        result[key] = result[key] || [];
        result[key].push(feature);
        return result;
      },{} as {[key:string]: DtoFeature[]});
  }

  getPreviewData(control: string, roleDetails: Role = {} as Role): DtoPreviewItem[]
  {
    switch(control){
      case 'Feature':
        return roleDetails.features.map(x=>({
          groupName: x.featureSetName,
          itemDisplay: null,
          itemName:x.featureName})) as DtoPreviewItem[];
      case 'SubCategory':
        return roleDetails.subCategories.map(x=>({
          groupName: x.moduleDisplayName,
          itemDisplay: x.subCategoryDisplayName,
          itemName:x.subCategoryName})) as DtoPreviewItem[];
      case 'Module':
        return roleDetails.modules.map(x=>({
          groupName: null,
          itemName:x.moduleName})) as DtoPreviewItem[];
      default:
        return [];
    }
  }

  goBack(){
    this.router.navigate(['/userCenter']);
  }
  openEditRolePopup(role: Role = {} as Role, packageId: number = 0){
    this.dialog.open(EditUserRoleComponent, {
      width: '1000px',
      data:{
        role: role,
        packageId: packageId,
        clientAccountId: this.clientAccountId,
        schoolId: this.schoolId
      }
    }).afterClosed().subscribe((popupEvent: any)=>{
      this.createTriggered = false;
      if(popupEvent?.event == MatDialogCloseEvent.SUCCESS){
        if(this.userRole.includes(this.rolesMaster.SchoolAdmin) || this.userRole.includes(this.rolesMaster.MatAdmin))
        {
          this.getRolesForAdmin(this.userEmail);
        }
        else
        {
          this.getRoles(this.clientAccountId);
        }
      }
      this.resetCreateTrigger.emit();
    });
  }

  activateDeactivateRole(role: Role){
    this.dialog.open(ConfirmComponent, {
      width: '375px',
      data: {
        status: role.isActive,
        message: "Are you sure want to" + (role.isActive ? " inactivate" : " activate") + " the role?"
      }
    }).afterClosed().subscribe((popupEvent: any)=>{
      if(popupEvent?.event == MatDialogCloseEvent.SUCCESS){
        this.schoolService.updateRoleStatus(role.roleId).subscribe((res)=>{
          if(res){
            this.toasterService.success("Role" + (role.isActive ? " activated" : " inactivated") + " successfully","Success");
            
            if(this.userRole.includes(this.rolesMaster.SchoolAdmin) || this.userRole.includes(this.rolesMaster.MatAdmin))
              this.getRolesForAdmin(this.userEmail);
            else
              this.getRoles(this.clientAccountId);
          }
        },(error: HttpErrorResponse)=>{
          if(error.error){
            this.toasterService.error(error.error, "Error");
          }
          role.isActive = !role.isActive;
        })
      }
      else{
        role.isActive = !role.isActive;
      }
    });
  }

  openDeleteRolePopup(role: Role){
    this.dialog.open(ConfirmComponent, {
      width: '375px',
      data: {
        status: true,
        message: "Are you sure want to delete the role?"
      }
    }).afterClosed().subscribe((popupEvent: any)=>{
      if(popupEvent?.event == MatDialogCloseEvent.SUCCESS){
        this.schoolService.deleteRole(role.roleId).subscribe((res)=>{
          if(res){
            this.toasterService.success("Role deleted successfully!","Success");
            if(this.userRole.includes(this.rolesMaster.SchoolAdmin) || this.userRole.includes(this.rolesMaster.MatAdmin))
              this.getRolesForAdmin(this.userEmail);
            else
              this.getRoles(this.clientAccountId);
          }
        },(error: HttpErrorResponse)=>{
          if(error.error){
            this.toasterService.error(error.error, "Error");
          }
        })
      }
    });
  }

requestToCreateRole(){
    let roleDetails = this.roleForm.value.roleDetails;
    let roleDetailsToSend = JSON.parse(JSON.stringify(roleDetails));
    for(var i=0; i<roleDetailsToSend.length; i++){
      roleDetailsToSend[i].roleId = roleDetailsToSend[i].roleId ? roleDetailsToSend[i].roleId : 0;
    }
    roleDetailsToSend[0].requestedRole = !!this.requestNewRole;
    roleDetailsToSend[0].schoolAdminEmail = this.requestNewRole ? this.userEmail : "";
    
    this.schoolService.createRole(roleDetailsToSend, this.clientAccountId, this.schoolId).subscribe((response: any)=>{
      if(response){
        this.toasterService.success( (this.requestNewRole ? 
          "Request to create a role sent successfully" : "Role created successfully" ),"Success");
        this.resetForm();
        this.requestNewRole = false;
        this.createNewRole = false;
        if(this.userRole.includes(this.rolesMaster.SchoolAdmin) || this.userRole.includes(this.rolesMaster.MatAdmin))
          this.getRolesForAdmin(this.userEmail);
        else
          this.getRoles(this.clientAccountId);
      }
    },(err: HttpErrorResponse)=>{
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    })
  }

  resetForm() {
    // Reset the form values
    this.roleForm.reset();
  
    // Clear the FormArray 'roleDetails'
    const roleDetailsArray = this.roleForm.get('roleDetails') as FormArray;
    while (roleDetailsArray && roleDetailsArray.length !== 0) {
      roleDetailsArray.removeAt(0); // Remove all controls one by one
    }
    this.sharedService.updateRoleFormValidity(this.roleForm);
  }

}
